import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import moment from "moment";
import { formatDistance } from "../config/defines";
import { ResumoClienteModel } from "../models/ResumoClienteModel";
import { LoginModel } from "../models/LoginModel";
import { MissaoLogDetalhesModel, MissaoLogImagensModel } from "../models/MissaoLogModel";

interface VooItem {
	dataString: string;
	missao?: { nome: string };
	piloto?: { nome: string };
	drone?: { nome: string };
	tempo: number;
	distancia: number;
	bateria: number;
	vento: number;
	temperatura: number;
}

interface generateVoosPDFParams {
	user: LoginModel;
	cliente: ResumoClienteModel;
	data: VooItem[];
	dataInicial: string;
	dataFinal: string;
	config: {
		pilotoResumo: boolean;
		pilotoVoos: boolean;
		pilotoTempo: boolean;
		droneResumo: boolean;
		droneVoos: boolean;
		droneTempo: boolean;
		missaoResumo: boolean;
		missaoVoos: boolean;
		missaoTempo: boolean;
		vooDetalhes: boolean;
	};
}

interface generateESGPDFParams {
	user: LoginModel;
	cliente: ResumoClienteModel;
	data: VooItem[];
	dataInicial: string;
	dataFinal: string;
	config: {
		pilotoResumo: boolean;
		pilotoVoos: boolean;
		pilotoTempo: boolean;
		droneResumo: boolean;
		droneVoos: boolean;
		droneTempo: boolean;
		missaoResumo: boolean;
		missaoVoos: boolean;
		missaoTempo: boolean;
		vooDetalhes: boolean;
	};
}

interface generateVooPDFParams {
	user: LoginModel;
	cliente: string;
	missaoLogID: number;
	data: MissaoLogDetalhesModel;
	imagens?: MissaoLogImagensModel[];
}

function formatDuration(seconds: number): string {
	const hours = Math.floor(seconds / 3600);
	const minutes = Math.floor((seconds % 3600) / 60);
	const secs = Math.floor(seconds % 60);
	return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
}

export function generateVoosPDF({ user, cliente, data, dataInicial, dataFinal, config }: generateVoosPDFParams): void {
	const img = new Image();
	img.src = "/logo_vigilante_escuro.png";
	img.onload = () => {
		const doc = new jsPDF({ unit: "px" });

		const pageWidth = doc.internal.pageSize.getWidth();

		const imageWidth = 88;
		const imageHeight = 40;

		const imageX = 30;
		const imageY = 30;

		let currentX = 30;
		let currentY = 90;

		doc.addImage(img, "PNG", imageX, imageY, imageWidth, imageHeight);

		doc.setFontSize(16);
		doc.text("Relatório de Voos", pageWidth - currentX, 32, { align: "right" });

		doc.setFontSize(10);
		doc.text(`${cliente.nome}`, pageWidth - currentX, 48, { align: "right" });
		doc.text(`${dataInicial} - ${dataFinal}`, pageWidth - currentX, 60, { align: "right" });

		// Cabeçalhos da tabela
		const voosHead = [["Data", "Missão", "Piloto", "Drone", "Tempo", "Distância", "Bateria", "Vento", "Temperatura"]];

		// Preparar dados para todas as tabelas em um único loop
		// Contadores por piloto
		let contadoresPilotos: { [key: string]: { nome: string; voos: number; tempo: number; distancia: number } } = {};

		// Contadores por drone
		let contadoresDrones: { [key: string]: { nome: string; voos: number; tempo: number; distancia: number } } = {};

		// Contadores por missão
		let contadoresMissoes: { [key: string]: { nome: string; voos: number; tempo: number; distancia: number } } = {};

		// Contadores por piloto e mês para voos
		let contadoresPilotosMesVoos: {
			[piloto: string]: {
				nome: string;
				meses: { [mes: string]: number };
			};
		} = {};

		// Contadores por piloto e mês para tempo
		let contadoresPilotosMesTempo: {
			[piloto: string]: {
				nome: string;
				meses: { [mes: string]: number };
			};
		} = {};

		// Contadores por drone e mês para voos
		let contadoresDronesMesVoos: {
			[drone: string]: {
				nome: string;
				meses: { [mes: string]: number };
			};
		} = {};

		// Contadores por drone e mês para tempo
		let contadoresDronesMesTempo: {
			[drone: string]: {
				nome: string;
				meses: { [mes: string]: number };
			};
		} = {};

		// Contadores por missão e mês para voos
		let contadoresMissoesMesVoos: {
			[missao: string]: {
				nome: string;
				meses: { [mes: string]: number };
			};
		} = {};

		// Contadores por missão e mês para tempo
		let contadoresMissoesMesTempo: {
			[missao: string]: {
				nome: string;
				meses: { [mes: string]: number };
			};
		} = {};

		// Conteúdo da tabela de voos
		const voosData: string[][] = [];

		data.forEach((item) => {
			// Dados para tabela principal de voos
			voosData.push([
				item.dataString,
				item.missao?.nome ?? "Voo Manual",
				item.piloto?.nome ?? "...",
				item.drone?.nome ?? "...",
				moment.utc(Number(item.tempo) * 1000).format("HH:mm:ss"),
				`${formatDistance(item.distancia).value.toFixed(1)} ${formatDistance(item.distancia).label}`,
				`${item.bateria}%`,
				`${parseFloat(item.vento.toFixed(3))} m/s`,
				`${item.temperatura} °C`,
			]);

			const pilotoNome = item.piloto?.nome ?? "...";
			const droneNome = item.drone?.nome ?? "...";
			const missaoNome = item.missao?.nome ?? "Voo Manual";
			const mesAno = moment(item.dataString, "DD/MM/YYYY").format("MM/YYYY");

			// Contadores pilotos
			if (!contadoresPilotos[pilotoNome]) {
				contadoresPilotos[pilotoNome] = {
					nome: pilotoNome,
					voos: 0,
					tempo: 0,
					distancia: 0,
				};
			}

			contadoresPilotos[pilotoNome].voos += 1;
			contadoresPilotos[pilotoNome].tempo += item.tempo;
			contadoresPilotos[pilotoNome].distancia += item.distancia;

			// Contadores drones
			if (!contadoresDrones[droneNome]) {
				contadoresDrones[droneNome] = {
					nome: droneNome,
					voos: 0,
					tempo: 0,
					distancia: 0,
				};
			}

			contadoresDrones[droneNome].voos += 1;
			contadoresDrones[droneNome].tempo += item.tempo;
			contadoresDrones[droneNome].distancia += item.distancia;

			// Contadores missões
			if (!contadoresMissoes[missaoNome]) {
				contadoresMissoes[missaoNome] = {
					nome: missaoNome,
					voos: 0,
					tempo: 0,
					distancia: 0,
				};
			}

			contadoresMissoes[missaoNome].voos += 1;
			contadoresMissoes[missaoNome].tempo += item.tempo;
			contadoresMissoes[missaoNome].distancia += item.distancia;

			// Contadores pilotos por mês - voos
			if (!contadoresPilotosMesVoos[pilotoNome]) {
				contadoresPilotosMesVoos[pilotoNome] = {
					nome: pilotoNome,
					meses: {},
				};
			}
			if (!contadoresPilotosMesVoos[pilotoNome].meses[mesAno]) {
				contadoresPilotosMesVoos[pilotoNome].meses[mesAno] = 0;
			}
			contadoresPilotosMesVoos[pilotoNome].meses[mesAno] += 1;

			// Contadores pilotos por mês - tempo
			if (!contadoresPilotosMesTempo[pilotoNome]) {
				contadoresPilotosMesTempo[pilotoNome] = {
					nome: pilotoNome,
					meses: {},
				};
			}
			if (!contadoresPilotosMesTempo[pilotoNome].meses[mesAno]) {
				contadoresPilotosMesTempo[pilotoNome].meses[mesAno] = 0;
			}
			contadoresPilotosMesTempo[pilotoNome].meses[mesAno] += item.tempo;

			// Contadores drones por mês - voos
			if (!contadoresDronesMesVoos[droneNome]) {
				contadoresDronesMesVoos[droneNome] = {
					nome: droneNome,
					meses: {},
				};
			}
			if (!contadoresDronesMesVoos[droneNome].meses[mesAno]) {
				contadoresDronesMesVoos[droneNome].meses[mesAno] = 0;
			}
			contadoresDronesMesVoos[droneNome].meses[mesAno] += 1;

			// Contadores drones por mês - tempo
			if (!contadoresDronesMesTempo[droneNome]) {
				contadoresDronesMesTempo[droneNome] = {
					nome: droneNome,
					meses: {},
				};
			}
			if (!contadoresDronesMesTempo[droneNome].meses[mesAno]) {
				contadoresDronesMesTempo[droneNome].meses[mesAno] = 0;
			}
			contadoresDronesMesTempo[droneNome].meses[mesAno] += item.tempo;

			// Contadores missões por mês - voos
			if (!contadoresMissoesMesVoos[missaoNome]) {
				contadoresMissoesMesVoos[missaoNome] = {
					nome: missaoNome,
					meses: {},
				};
			}
			if (!contadoresMissoesMesVoos[missaoNome].meses[mesAno]) {
				contadoresMissoesMesVoos[missaoNome].meses[mesAno] = 0;
			}
			contadoresMissoesMesVoos[missaoNome].meses[mesAno] += 1;

			// Contadores missões por mês - tempo
			if (!contadoresMissoesMesTempo[missaoNome]) {
				contadoresMissoesMesTempo[missaoNome] = {
					nome: missaoNome,
					meses: {},
				};
			}
			if (!contadoresMissoesMesTempo[missaoNome].meses[mesAno]) {
				contadoresMissoesMesTempo[missaoNome].meses[mesAno] = 0;
			}
			contadoresMissoesMesTempo[missaoNome].meses[mesAno] += item.tempo;
		});

		// Obter todos os meses únicos e ordená-los
		const todosOsMeses = new Set<string>();
		[
			...Object.values(contadoresPilotosMesVoos),
			...Object.values(contadoresPilotosMesTempo),
			...Object.values(contadoresDronesMesVoos),
			...Object.values(contadoresDronesMesTempo),
			...Object.values(contadoresMissoesMesVoos),
			...Object.values(contadoresMissoesMesTempo),
		].forEach((item) => {
			Object.keys(item.meses).forEach((mes) => todosOsMeses.add(mes));
		});
		const mesesOrdenados = Array.from(todosOsMeses).sort((a, b) => {
			const [mesA, anoA] = a.split("/");
			const [mesB, anoB] = b.split("/");
			if (anoA !== anoB) return parseInt(anoA) - parseInt(anoB);
			return parseInt(mesA) - parseInt(mesB);
		});

		// Primeira tabela - Resumo Pilotos
		if (config.pilotoResumo) {
			doc.setFontSize(12);
			doc.text("Resumo Pilotos", currentX, currentY);
			currentY += doc.getFontSize();

			const pilotosHead = [["Piloto", "Voos", "Tempo", "Distância"]];

			const pilotosData = Object.values(contadoresPilotos).map((piloto) => [
				piloto.nome,
				piloto.voos.toString(),
				formatDuration(piloto.tempo),
				`${formatDistance(piloto.distancia).value.toFixed(1)} ${formatDistance(piloto.distancia).label}`,
			]);

			// Calcular totais para pilotos
			const totalVoosPilotos = Object.values(contadoresPilotos).reduce((acc, piloto) => acc + piloto.voos, 0);
			const totalTempoPilotos = Object.values(contadoresPilotos).reduce((acc, piloto) => acc + piloto.tempo, 0);
			const totalDistanciaPilotos = Object.values(contadoresPilotos).reduce((acc, piloto) => acc + piloto.distancia, 0);

			const pilotosFoot = [
				["TOTAL", totalVoosPilotos.toString(), formatDuration(totalTempoPilotos), `${formatDistance(totalDistanciaPilotos).value.toFixed(1)} ${formatDistance(totalDistanciaPilotos).label}`],
			];

			autoTable(doc, {
				head: pilotosHead,
				body: pilotosData,
				foot: pilotosFoot,
				startY: currentY,
				headStyles: { fillColor: "#22293f" },
				footStyles: { fillColor: "#fff", textColor: "#000" },
				styles: { fontSize: 9 },
				theme: "striped",
			});

			currentY = (doc as any).lastAutoTable.finalY + 20;
		}

		// Segunda tabela - Pilotos Voos por Mês
		if (config.pilotoVoos) {
			doc.setFontSize(12);
			doc.text("Pilotos - Voos por Mês", currentX, currentY);
			currentY += doc.getFontSize();

			const pilotosVoosMesHead = [["Piloto", ...mesesOrdenados]];

			const pilotosVoosMesData = Object.values(contadoresPilotosMesVoos).map((piloto) => {
				const row = [piloto.nome];
				mesesOrdenados.forEach((mes) => {
					const voos = piloto.meses[mes] || 0;
					row.push(voos.toString());
				});
				return row;
			});

			// Calcular totais por mês para voos de pilotos
			const totalVoosPorMes = ["TOTAL"];
			mesesOrdenados.forEach((mes) => {
				const totalMes = Object.values(contadoresPilotosMesVoos).reduce((acc, piloto) => acc + (piloto.meses[mes] || 0), 0);
				totalVoosPorMes.push(totalMes.toString());
			});
			const pilotosVoosMesFoot = [totalVoosPorMes];

			autoTable(doc, {
				head: pilotosVoosMesHead,
				body: pilotosVoosMesData,
				foot: pilotosVoosMesFoot,
				startY: currentY,
				headStyles: { fillColor: "#22293f" },
				footStyles: { fillColor: "#fff", textColor: "#000" },
				styles: { fontSize: 8 },
			});

			currentY = (doc as any).lastAutoTable.finalY + 20;
		}

		// Terceira tabela - Pilotos Tempo por Mês
		if (config.pilotoTempo) {
			doc.setFontSize(12);
			doc.text("Pilotos - Tempo por Mês", currentX, currentY);
			currentY += doc.getFontSize();

			const pilotosTempoMesHead = [["Piloto", ...mesesOrdenados]];

			const pilotosTempoMesData = Object.values(contadoresPilotosMesTempo).map((piloto) => {
				const row = [piloto.nome];
				mesesOrdenados.forEach((mes) => {
					const tempo = piloto.meses[mes] || 0;
					row.push(formatDuration(tempo));
				});
				return row;
			});

			// Calcular totais por mês para tempo de pilotos
			const totalTempoPorMes = ["TOTAL"];
			mesesOrdenados.forEach((mes) => {
				const totalMes = Object.values(contadoresPilotosMesTempo).reduce((acc, piloto) => acc + (piloto.meses[mes] || 0), 0);
				totalTempoPorMes.push(formatDuration(totalMes));
			});
			const pilotosTempoMesFoot = [totalTempoPorMes];

			autoTable(doc, {
				head: pilotosTempoMesHead,
				body: pilotosTempoMesData,
				foot: pilotosTempoMesFoot,
				startY: currentY,
				headStyles: { fillColor: "#22293f" },
				footStyles: { fillColor: "#fff", textColor: "#000" },
				styles: { fontSize: 7 },
			});

			currentY = (doc as any).lastAutoTable.finalY + 20;
		}

		// Quarta tabela - Resumo Drones
		if (config.droneResumo) {
			doc.setFontSize(12);
			doc.text("Resumo Drones", currentX, currentY);
			currentY += doc.getFontSize();

			const dronesHead = [["Drone", "Voos", "Tempo", "Distância"]];

			const dronesData = Object.values(contadoresDrones).map((drone) => [
				drone.nome,
				drone.voos.toString(),
				formatDuration(drone.tempo),
				`${formatDistance(drone.distancia).value.toFixed(1)} ${formatDistance(drone.distancia).label}`,
			]);

			// Calcular totais para drones
			const totalVoosDrones = Object.values(contadoresDrones).reduce((acc, drone) => acc + drone.voos, 0);
			const totalTempoDrones = Object.values(contadoresDrones).reduce((acc, drone) => acc + drone.tempo, 0);
			const totalDistanciaDrones = Object.values(contadoresDrones).reduce((acc, drone) => acc + drone.distancia, 0);

			const dronesFoot = [
				["TOTAL", totalVoosDrones.toString(), formatDuration(totalTempoDrones), `${formatDistance(totalDistanciaDrones).value.toFixed(1)} ${formatDistance(totalDistanciaDrones).label}`],
			];

			autoTable(doc, {
				head: dronesHead,
				body: dronesData,
				foot: dronesFoot,
				startY: currentY,
				headStyles: { fillColor: "#22293f" },
				footStyles: { fillColor: "#fff", textColor: "#000" },
				styles: { fontSize: 9 },
				theme: "striped",
			});

			currentY = (doc as any).lastAutoTable.finalY + 20;
		}

		// Quinta tabela - Drones Voos por Mês
		if (config.droneVoos) {
			doc.setFontSize(12);
			doc.text("Drones - Voos por Mês", currentX, currentY);
			currentY += doc.getFontSize();

			const dronesVoosMesHead = [["Drone", ...mesesOrdenados]];

			const dronesVoosMesData = Object.values(contadoresDronesMesVoos).map((drone) => {
				const row = [drone.nome];
				mesesOrdenados.forEach((mes) => {
					const voos = drone.meses[mes] || 0;
					row.push(voos.toString());
				});
				return row;
			});

			// Calcular totais por mês para voos de drones
			const totalVoosDronesPorMes = ["TOTAL"];
			mesesOrdenados.forEach((mes) => {
				const totalMes = Object.values(contadoresDronesMesVoos).reduce((acc, drone) => acc + (drone.meses[mes] || 0), 0);
				totalVoosDronesPorMes.push(totalMes.toString());
			});
			const dronesVoosMesFoot = [totalVoosDronesPorMes];

			autoTable(doc, {
				head: dronesVoosMesHead,
				body: dronesVoosMesData,
				foot: dronesVoosMesFoot,
				startY: currentY,
				headStyles: { fillColor: "#22293f" },
				footStyles: { fillColor: "#fff", textColor: "#000" },
				styles: { fontSize: 8 },
			});

			currentY = (doc as any).lastAutoTable.finalY + 20;
		}

		// Sexta tabela - Drones Tempo por Mês
		if (config.droneTempo) {
			doc.setFontSize(12);
			doc.text("Drones - Tempo por Mês", currentX, currentY);
			currentY += doc.getFontSize();

			const dronesTempoMesHead = [["Drone", ...mesesOrdenados]];

			const dronesTempoMesData = Object.values(contadoresDronesMesTempo).map((drone) => {
				const row = [drone.nome];
				mesesOrdenados.forEach((mes) => {
					const tempo = drone.meses[mes] || 0;
					row.push(formatDuration(tempo));
				});
				return row;
			});

			// Calcular totais por mês para tempo de drones
			const totalTempoDronesPorMes = ["TOTAL"];
			mesesOrdenados.forEach((mes) => {
				const totalMes = Object.values(contadoresDronesMesTempo).reduce((acc, drone) => acc + (drone.meses[mes] || 0), 0);
				totalTempoDronesPorMes.push(formatDuration(totalMes));
			});
			const dronesTempoMesFoot = [totalTempoDronesPorMes];

			autoTable(doc, {
				head: dronesTempoMesHead,
				body: dronesTempoMesData,
				foot: dronesTempoMesFoot,
				startY: currentY,
				headStyles: { fillColor: "#22293f" },
				footStyles: { fillColor: "#fff", textColor: "#000" },
				styles: { fontSize: 7 },
			});

			currentY = (doc as any).lastAutoTable.finalY + 20;
		}

		// Sétima tabela - Resumo Missões
		if (config.missaoResumo) {
			doc.setFontSize(12);
			doc.text("Resumo Missões", currentX, currentY);
			currentY += doc.getFontSize();

			const missoesHead = [["Missão", "Voos", "Tempo", "Distância"]];

			const missoesData = Object.values(contadoresMissoes).map((missao) => [
				missao.nome,
				missao.voos.toString(),
				formatDuration(missao.tempo),
				`${formatDistance(missao.distancia).value.toFixed(1)} ${formatDistance(missao.distancia).label}`,
			]);

			// Calcular totais para missões
			const totalVoosMissoes = Object.values(contadoresMissoes).reduce((acc, missao) => acc + missao.voos, 0);
			const totalTempoMissoes = Object.values(contadoresMissoes).reduce((acc, missao) => acc + missao.tempo, 0);
			const totalDistanciaMissoes = Object.values(contadoresMissoes).reduce((acc, missao) => acc + missao.distancia, 0);

			const missoesFoot = [
				["TOTAL", totalVoosMissoes.toString(), formatDuration(totalTempoMissoes), `${formatDistance(totalDistanciaMissoes).value.toFixed(1)} ${formatDistance(totalDistanciaMissoes).label}`],
			];

			autoTable(doc, {
				head: missoesHead,
				body: missoesData,
				foot: missoesFoot,
				startY: currentY,
				headStyles: { fillColor: "#22293f" },
				footStyles: { fillColor: "#fff", textColor: "#000" },
				styles: { fontSize: 9 },
				theme: "striped",
			});

			currentY = (doc as any).lastAutoTable.finalY + 20;
		}

		// Oitava tabela - Missões Voos por Mês
		if (config.missaoVoos) {
			doc.setFontSize(12);
			doc.text("Missões - Voos por Mês", currentX, currentY);
			currentY += doc.getFontSize();

			const missoesVoosMesHead = [["Missão", ...mesesOrdenados]];

			const missoesVoosMesData = Object.values(contadoresMissoesMesVoos).map((missao) => {
				const row = [missao.nome];
				mesesOrdenados.forEach((mes) => {
					const voos = missao.meses[mes] || 0;
					row.push(voos.toString());
				});
				return row;
			});

			// Calcular totais por mês para voos de missões
			const totalVoosMissoesPorMes = ["TOTAL"];
			mesesOrdenados.forEach((mes) => {
				const totalMes = Object.values(contadoresMissoesMesVoos).reduce((acc, missao) => acc + (missao.meses[mes] || 0), 0);
				totalVoosMissoesPorMes.push(totalMes.toString());
			});
			const missoesVoosMesFoot = [totalVoosMissoesPorMes];

			autoTable(doc, {
				head: missoesVoosMesHead,
				body: missoesVoosMesData,
				foot: missoesVoosMesFoot,
				startY: currentY,
				headStyles: { fillColor: "#22293f" },
				footStyles: { fillColor: "#fff", textColor: "#000" },
				styles: { fontSize: 8 },
			});

			currentY = (doc as any).lastAutoTable.finalY + 20;
		}

		// Nona tabela - Missões Tempo por Mês
		if (config.missaoTempo) {
			doc.setFontSize(12);
			doc.text("Missões - Tempo por Mês", currentX, currentY);
			currentY += doc.getFontSize();

			const missoesTempoMesHead = [["Missão", ...mesesOrdenados]];

			const missoesTempoMesData = Object.values(contadoresMissoesMesTempo).map((missao) => {
				const row = [missao.nome];
				mesesOrdenados.forEach((mes) => {
					const tempo = missao.meses[mes] || 0;
					row.push(formatDuration(tempo));
				});
				return row;
			});

			// Calcular totais por mês para tempo de missões
			const totalTempoMissoesPorMes = ["TOTAL"];
			mesesOrdenados.forEach((mes) => {
				const totalMes = Object.values(contadoresMissoesMesTempo).reduce((acc, missao) => acc + (missao.meses[mes] || 0), 0);
				totalTempoMissoesPorMes.push(formatDuration(totalMes));
			});
			const missoesTempoMesFoot = [totalTempoMissoesPorMes];

			autoTable(doc, {
				head: missoesTempoMesHead,
				body: missoesTempoMesData,
				foot: missoesTempoMesFoot,
				startY: currentY,
				headStyles: { fillColor: "#22293f" },
				footStyles: { fillColor: "#fff", textColor: "#000" },
				styles: { fontSize: 7 },
			});

			currentY = (doc as any).lastAutoTable.finalY + 20;
		}

		// Décima tabela - Voos detalhados
		if (config.vooDetalhes) {
			doc.setFontSize(12);
			doc.text("Voos Detalhados", currentX, currentY);
			currentY += doc.getFontSize();

			autoTable(doc, {
				head: voosHead,
				body: voosData,
				startY: currentY,
				headStyles: { fillColor: "#22293f" },
				styles: { fontSize: 7 },
				theme: "striped",
			});

			currentY = (doc as any).lastAutoTable.finalY + 20;
		}

		// Data de emissão
		doc.setFontSize(8);
		doc.text("Emitido por " + user.nome + " em " + moment().format("DD/MM/YYYY HH:mm:ss"), currentX, currentY);

		// Abrir o PDF em nova janela
		// doc.output("dataurlnewwindow");
		doc.save(`Relatório_Voos_${dataInicial.replaceAll("/", "")}_${dataFinal.replaceAll("/", "")}.pdf`);
		doc.close();
	};
}

export function generateESGPDF({ user, cliente, data, dataInicial, dataFinal, config }: generateESGPDFParams): void {
	const img = new Image();
	img.src = "/logo_vigilante_escuro.png";
	img.onload = () => {
		const doc = new jsPDF({ unit: "px" });

		const pageWidth = doc.internal.pageSize.getWidth();

		const imageWidth = 88;
		const imageHeight = 40;
		const imageX = 30;
		const imageY = 30;
		let currentX = 30;
		let currentY = 90;
		const defaultWidth = pageWidth - currentX * 2;

		// Calcular dados operacionais
		const totalVoos = data.length;
		const tempoTotalSegundos = data.reduce((acc, item) => acc + item.tempo, 0);
		const tempoTotalHoras = Math.floor(tempoTotalSegundos / 3600);
		const tempoTotalMinutos = Math.floor((tempoTotalSegundos % 3600) / 60);
		const tempoTotalFormatado = `${tempoTotalHoras}h ${tempoTotalMinutos}min`;
		const distanciaTotalKm = data.reduce((acc, item) => acc + item.distancia, 0) / 1000; // converter para km

		// Fatores de emissão (kg CO2/km)
		const fatoresEmissao = {
			drone: 0.01,
			moto: 0.1,
			carro: 0.17,
			pickup: 0.25,
		};

		// Calcular emissões
		const emissoesKg = {
			drone: Math.round(distanciaTotalKm * fatoresEmissao.drone * 100) / 100,
			moto: Math.round(distanciaTotalKm * fatoresEmissao.moto * 100) / 100,
			carro: Math.round(distanciaTotalKm * fatoresEmissao.carro * 100) / 100,
			pickup: Math.round(distanciaTotalKm * fatoresEmissao.pickup * 100) / 100,
		};

		// Calcular reduções
		const reducoes = {
			moto: {
				absoluta: Math.round((emissoesKg.moto - emissoesKg.drone) * 100) / 100,
				percentual: Math.round(((emissoesKg.moto - emissoesKg.drone) / emissoesKg.moto) * 100) || "-",
			},
			carro: {
				absoluta: Math.round((emissoesKg.carro - emissoesKg.drone) * 100) / 100,
				percentual: Math.round(((emissoesKg.carro - emissoesKg.drone) / emissoesKg.carro) * 100) || "-",
			},
			pickup: {
				absoluta: Math.round((emissoesKg.pickup - emissoesKg.drone) * 100) / 100,
				percentual: Math.round(((emissoesKg.pickup - emissoesKg.drone) / emissoesKg.pickup) * 100) || "-",
			},
		};

		// Adicionar logo e cabeçalho
		doc.addImage(img, "PNG", imageX, imageY, imageWidth, imageHeight);

		doc.setFontSize(16);
		doc.text("Relatório ESG: Uso de Drones vs. Veículos", pageWidth - currentX, 32, { align: "right" });

		doc.setFontSize(10);
		doc.text(`${cliente.nome}`, pageWidth - currentX, 48, { align: "right" });
		doc.text(`${dataInicial} - ${dataFinal}`, pageWidth - currentX, 60, { align: "right" });

		// Informações Gerais
		doc.setFontSize(14);
		doc.text("Informações Gerais", currentX, currentY);
		currentY += 20;

		autoTable(doc, {
			body: [
				["Título", "Análise Comparativa de Impactos Ambientais, Sociais e de Governança"],
				["Período", `${dataInicial} - ${dataFinal}`],
				["Elaborado por", cliente.nome],
				["Data", moment().format("DD/MM/YYYY")],
			],
			startY: currentY,
			styles: { fontSize: 10 },
			theme: "striped",
		});

		currentY = (doc as any).lastAutoTable.finalY + 20;

		// 1. Introdução
		doc.setFontSize(14);
		doc.text("1. Introdução", currentX, currentY);
		currentY += 15;

		// prettier-ignore
		const introducaoTexto = `Este relatório apresenta uma análise ESG (Environmental, Social, Governance) comparando o uso de drones com veículos tradicionais (moto, carro e pickup diesel) em operações logísticas. Com base nos dados operacionais fornecidos — ${totalVoos} voos, ${tempoTotalFormatado} de voo e ${Math.round(distanciaTotalKm * 100) / 100} km voados —, avaliamos os benefícios ambientais (redução de emissões de CO2), sociais (segurança e eficiência) e de governança (inovação e conformidade). O objetivo é demonstrar como a substituição de veículos por drones contribui para práticas mais sustentáveis e alinhadas aos princípios ESG.`;
		doc.setFontSize(10);
		let lines = doc.splitTextToSize(introducaoTexto, defaultWidth);
		doc.text(lines, currentX, currentY, { maxWidth: defaultWidth });
		currentY = currentY + lines.length * 10;

		currentY += 10;

		// 2. Metodologia
		doc.setFontSize(14);
		doc.text("2. Metodologia", currentX, currentY);
		currentY += 15;

		doc.setFontSize(12);
		doc.text("2.1. Dados Operacionais", currentX, currentY);
		currentY += 12;

		doc.setFontSize(10);
		doc.text("Os dados utilizados no relatório são:", currentX, currentY);
		currentY += 12;
		doc.text(`• Total de voos: ${totalVoos}`, currentX + 15, currentY);
		currentY += 12;
		doc.text(`• Tempo total de voo: ${tempoTotalFormatado}`, currentX + 15, currentY);
		currentY += 12;
		doc.text(`• Total de quilômetros voados: ${Math.round(distanciaTotalKm * 100) / 100} km`, currentX + 15, currentY);
		currentY += 15;

		doc.setFontSize(12);
		doc.text("2.2. Fatores de Emissão", currentX, currentY);
		currentY += 12;

		doc.setFontSize(10);
		doc.text("Para comparar os impactos ambientais, utilizamos os seguintes fatores de emissão (em kg de CO2 por km):", currentX, currentY);
		currentY += 12;
		doc.text("• Moto: 0,1 kg CO2/km", currentX + 15, currentY);
		currentY += 10;
		doc.text("• Carro (diesel): 0,17 kg CO2/km", currentX + 15, currentY);
		currentY += 10;
		doc.text("• Pickup (diesel): 0,25 kg CO2/km", currentX + 15, currentY);
		currentY += 10;
		doc.text("• Drone (elétrico): 0,01 kg CO2/km (assumindo energia da rede elétrica)", currentX + 15, currentY);
		currentY += 20;

		// Adicionar nova página se necessário
		if (currentY > 500) {
			doc.addPage();
			currentY = 30;
		}

		// 3. Resultados Ambientais
		doc.setFontSize(14);
		doc.text("3. Resultados Ambientais", currentX, currentY);
		currentY += 15;

		doc.setFontSize(12);
		doc.text("3.1. Emissões de CO2", currentX, currentY);
		currentY += 12;

		doc.setFontSize(10);
		doc.text(`Com base nos ${Math.round(distanciaTotalKm * 100) / 100} km voados, as emissões de CO2 foram calculadas como segue:`, currentX, currentY);
		currentY += 12;
		doc.text(`• Drones: ${Math.round(distanciaTotalKm * 100) / 100} × 0,01 = ${emissoesKg.drone} kg de CO2`, currentX + 15, currentY);
		currentY += 10;
		doc.text(`• Moto: ${Math.round(distanciaTotalKm * 100) / 100} × 0,1 = ${emissoesKg.moto} kg de CO2`, currentX + 15, currentY);
		currentY += 10;
		doc.text(`• Carro (diesel): ${Math.round(distanciaTotalKm * 100) / 100} × 0,17 = ${emissoesKg.carro} kg de CO2`, currentX + 15, currentY);
		currentY += 10;
		doc.text(`• Pickup (diesel): ${Math.round(distanciaTotalKm * 100) / 100} × 0,25 = ${emissoesKg.pickup} kg de CO2`, currentX + 15, currentY);
		currentY += 15;

		doc.setFontSize(12);
		doc.text("3.2. Redução de Emissões", currentX, currentY);
		currentY += 12;

		doc.setFontSize(10);
		doc.text("A substituição de veículos por drones resultou nas seguintes reduções:", currentX, currentY);
		currentY += 12;
		doc.text(`• Comparado à moto: ${emissoesKg.moto} - ${emissoesKg.drone} = ${reducoes.moto.absoluta} kg (${reducoes.moto.percentual}% de redução)`, currentX + 15, currentY);
		currentY += 10;
		doc.text(`• Comparado ao carro: ${emissoesKg.carro} - ${emissoesKg.drone} = ${reducoes.carro.absoluta} kg (${reducoes.carro.percentual}% de redução)`, currentX + 15, currentY);
		currentY += 10;
		doc.text(`• Comparado à pickup: ${emissoesKg.pickup} - ${emissoesKg.drone} = ${reducoes.pickup.absoluta} kg (${reducoes.pickup.percentual}% de redução)`, currentX + 15, currentY);
		currentY += 15;

		doc.setFontSize(12);
		doc.text("3.3. Tabela Comparativa", currentX, currentY);
		currentY += 12;

		// Tabela comparativa de emissões
		const emissaoHead = [["Veículo", "Emissões (kg CO2)", "Redução (kg CO2)", "Redução (%)"]];
		const emissaoData = [
			["Drone", emissoesKg.drone.toString(), "--", "--"],
			["Moto", emissoesKg.moto.toString(), reducoes.moto.absoluta.toString(), `${reducoes.moto.percentual}%`],
			["Carro (diesel)", emissoesKg.carro.toString(), reducoes.carro.absoluta.toString(), `${reducoes.carro.percentual}%`],
			["Pickup (diesel)", emissoesKg.pickup.toString(), reducoes.pickup.absoluta.toString(), `${reducoes.pickup.percentual}%`],
		];

		autoTable(doc, {
			head: emissaoHead,
			body: emissaoData,
			startY: currentY,
			headStyles: { fillColor: "#22293f" },
			styles: { fontSize: 10 },
			theme: "striped",
		});

		currentY = (doc as any).lastAutoTable.finalY + 20;

		// Adicionar nova página se necessário
		if (currentY > 500) {
			doc.addPage();
			currentY = 30;
		}

		// 4. Resultados Sociais
		doc.setFontSize(14);
		doc.text("4. Resultados Sociais", currentX, currentY);
		currentY += 15;

		const sociaisTexto = [
			"O uso de drones oferece benefícios sociais significativos:",
			"• Segurança: Redução de acidentes de trânsito, já que drones operam em espaço aéreo controlado, eliminando riscos associados ao tráfego rodoviário.",
			`• Eficiência: Com ${tempoTotalFormatado} de voo, os drones proporcionam entregas mais rápidas, especialmente em áreas urbanas congestionadas ou rurais remotas.`,
			"• Acessibilidade: Drones podem alcançar áreas de difícil acesso, promovendo inclusão e atendimento a comunidades isoladas.",
		];

		doc.setFontSize(10);
		sociaisTexto.forEach((linha, index) => {
			let lines = doc.splitTextToSize(linha, defaultWidth);
			doc.text(lines, index === 0 ? currentX : currentX + 15, currentY, { maxWidth: defaultWidth });
			currentY = currentY + lines.length * 10;
		});

		currentY += 10;

		// 5. Resultados de Governança
		doc.setFontSize(14);
		doc.text("5. Resultados de Governança", currentX, currentY);
		currentY += 15;

		const governancaTexto = [
			"A adoção de drones reflete práticas de governança sólidas:",
			"• Inovação: Investimento em tecnologia de ponta, alinhado às metas de sustentabilidade e modernização.",
			"• Conformidade: As operações com drones seguem as regulamentações da Agência Nacional de Aviação Civil (ANAC) no Brasil, garantindo conformidade legal.",
			"• Transparência: Os dados operacionais são monitorados e registrados, permitindo auditorias e relatórios precisos.",
		];

		doc.setFontSize(10);
		governancaTexto.forEach((linha, index) => {
			let lines = doc.splitTextToSize(linha, defaultWidth);
			doc.text(lines, index === 0 ? currentX : currentX + 15, currentY, { maxWidth: defaultWidth });
			currentY = currentY + lines.length * 10;
		});

		currentY += 10;

		// 6. Conclusão
		doc.setFontSize(14);
		doc.text("6. Conclusão", currentX, currentY);
		currentY += 15;

		// prettier-ignore
		const conclusaoTexto = `A substituição de veículos tradicionais por drones resulta em benefícios significativos no âmbito ESG. Ambientalmente, os drones reduziram as emissões de CO2 em até ${reducoes.pickup.percentual}% em comparação com pickups diesel, contribuindo para a mitigação das mudanças climáticas. Socialmente, a operação com drones melhora a segurança e a eficiência, além de promover acessibilidade. Em termos de governança, o uso de drones demonstra compromisso com inovação e conformidade regulatória. Recomendamos a expansão do uso de drones em operações logísticas para maximizar esses benefícios, acompanhada de investimentos em energia renovável para os drones, a fim de reduzir ainda mais o impacto ambiental.`;
		doc.setFontSize(10);
		let conclusaoTextoLines = doc.splitTextToSize(conclusaoTexto, defaultWidth);
		doc.text(conclusaoTextoLines, currentX, currentY, { maxWidth: defaultWidth });
		currentY = currentY + conclusaoTextoLines.length * 9;

		currentY += 10;

		// 7. Recomendações
		doc.setFontSize(14);
		doc.text("7. Recomendações", currentX, currentY);
		currentY += 15;

		const recomendacoesTexto = [
			"• Substituir gradualmente frotas de veículos por drones em rotas viáveis.",
			"• Investir em fontes de energia renovável para carregar drones, reduzindo a pegada de carbono.",
			"• Monitorar continuamente os dados operacionais para relatórios ESG futuros.",
			"• Garantir treinamento adequado para operadores de drones, mantendo a conformidade com regulamentações.",
		];

		doc.setFontSize(10);
		recomendacoesTexto.forEach((linha) => {
			doc.text(linha, currentX + 15, currentY);
			currentY += 12;
		});

		currentY += 20;

		// Data de emissão
		doc.setFontSize(8);
		doc.text("Emitido por " + user.nome + " em " + moment().format("DD/MM/YYYY HH:mm:ss"), currentX, currentY);

		// Salvar o PDF
		// doc.output("dataurlnewwindow");
		doc.save(`Relatório_ESG_${dataInicial.replaceAll("/", "")}_${dataFinal.replaceAll("/", "")}.pdf`);
		doc.close();
	};
}

export function generateVooPDF({ user, cliente, missaoLogID, data, imagens }: generateVooPDFParams) {
	const img = new Image();
	img.src = "/logo_vigilante_escuro.png";
	img.onload = () => {
		const doc = new jsPDF({ unit: "px" });

		const pageWidth = doc.internal.pageSize.getWidth();

		const imageWidth = 88;
		const imageHeight = 40;

		const imageX = 30;
		const imageY = 30;

		let currentX = 30;
		let currentY = 90;

		doc.addImage(img, "PNG", imageX, imageY, imageWidth, imageHeight);

		doc.setFontSize(16);
		doc.text("Relatório de Voo", pageWidth - currentX, 32, { align: "right" });

		doc.setFontSize(10);
		doc.text(`${cliente}`, pageWidth - currentX, 48, { align: "right" });
		doc.text(`Voo ${missaoLogID}`, pageWidth - currentX, 60, { align: "right" });

		doc.setFontSize(12);
		doc.text("Informações", currentX, currentY);
		currentY += doc.getFontSize();

		autoTable(doc, {
			body: [
				["Missão", `${data.missao?.nome ?? "Voo Manual"}`],
				["Piloto", `${data.piloto?.nome ?? "..."}`],
				["Drone", `${data.drone?.nome ?? "..."}`],
				["Data", `${data.data.dateString ?? ""}`],
				["Tempo", `${moment.utc(Number(data.tempo) * 1000).format("HH:mm:ss")}`],
				["Distância", `${formatDistance(data.distancia).value.toFixed(1)} ${formatDistance(data.distancia).label}`],
				["Bateria", `${data.bateria}%`],
				["Vento", `${parseFloat(data.vento.toFixed(3))} m/s`],
				["Temperatura", `${data.temperatura} °C`],
				["Observações", `${data.observacao ?? ""}`],
				["Acesso", `${window.location.origin}/voo/${missaoLogID}`],
			],
			startY: currentY,
			styles: { fontSize: 10 },
			theme: "striped",
		});

		currentY = (doc as any).lastAutoTable.finalY + 20;

		if (imagens && imagens?.length > 0) {
			doc.setFontSize(12);
			doc.text("Imagens", currentX, currentY);
			currentY += doc.getFontSize();

			const imagensHead = [["Imagem", "Dados"]];
			const imagensData = imagens?.map((imagem) => ["", `- Altura: ${imagem.height}m \n- Latitude: ${imagem.latitude}\n- Longitude: ${imagem.longitude}\n- Ocorrência: ${imagem.ocorrencia}`]);

			autoTable(doc, {
				head: imagensHead,
				body: imagensData,
				startY: currentY,
				headStyles: { fillColor: "#22293f" },
				styles: { fontSize: 10 },
				columnStyles: {
					0: { minCellWidth: 260, minCellHeight: 130 },
				},
				rowPageBreak: "avoid",
				theme: "striped",
				didDrawCell: function (data) {
					// Verifica se está na coluna da imagem e se existe imagem para desenhar
					if (data.section === "body" && data.column.index === 0 && imagens && data.row.index < imagens.length) {
						const img = imagens[data.row.index];
						if (img && img.fileStrBase64) {
							const size = img.fileURL.split(";");
							let width = Number(size[0]);
							let height = Number(size[1]);

							// scale width height to ocupie de max width or max height from cell
							const maxWidth = data.cell.width - 10;
							const maxHeight = data.cell.height - 10;
							const scale = Math.min(maxWidth / width, maxHeight / height);
							width *= scale;
							height *= scale;

							const x = data.cell.x + data.cell.width / 2 - width / 2;
							const y = data.cell.y + data.cell.height / 2 - height / 2;

							doc.addImage(img.fileStrBase64, "JPEG", x, y, width, height, "", "SLOW");
						}
					}
				},
			});
		}

		// Data de emissão
		currentY = (doc as any).lastAutoTable.finalY + 20;
		doc.setFontSize(10);
		doc.text("Emitido por " + user.nome + " em " + moment().format("DD/MM/YYYY HH:mm:ss"), currentX, currentY);

		// Salvar o PDF
		// doc.output("dataurlnewwindow");
		doc.save(`Relatório_Voo_${missaoLogID}.pdf`);
		doc.close();
	};
}
